import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Printer, X, History, Calendar } from "lucide-react";
import Layout from "./layout";
import TemplateDetailModal from "../Modal/TemplateDetailModal";
import EditTemplateModal from "../Modal/editTamplateModal";
import HealthResultModal from "../Modal/HealthResultModal";
import SendHealthResultModal from "../Modal/SendHealthResultModal";
import HealthReportModal from "../Modal/HealthReportModal";
import { useNavigate } from "react-router-dom";
import { healthRecordsAPI, templateAPI, normalRangeAPI, healthRecommendationAPI, memberAPI, referenceDataAPI } from "../services/api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { createGlobalStyle } from 'styled-components';

// Custom styles สำหรับ DatePicker
const DatePickerStyles = createGlobalStyle`
  .react-datepicker {
    font-family: inherit;
    border: 2px solid #00B5B8;
    border-radius: 0.5rem;
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  }

  .react-datepicker__header {
    background-color: #00B5B8;
    border-bottom: none;
    border-radius: 0.5rem 0.5rem 0 0;
    padding: 1rem 0.75rem 0.75rem;
  }

  .react-datepicker__current-month {
    color: #FFFFFF;
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .react-datepicker__day-name {
    color: #FFFFFF;
    font-size: 0.875rem;
    font-weight: 600;
    width: 2rem;
    line-height: 2rem;
    margin: 0.166rem;
  }

  .react-datepicker__day {
    color: #1F2937;
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: 0.375rem;
    width: 2rem;
    line-height: 2rem;
    margin: 0.166rem;
    transition: all 0.2s;

    &:hover {
      background-color: #00B5B8;
      color: white;
      transform: scale(1.1);
    }
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background-color: #00B5B8;
    color: white;
    font-weight: 700;
    border: 2px solid #00B5B8;

    &:hover {
      background-color: #008F92;
      border-color: #008F92;
    }
  }

  .react-datepicker__day--disabled {
    color: #D1D5DB;
    cursor: not-allowed;
    opacity: 0.5;

    &:hover {
      background-color: transparent;
      transform: none;
    }
  }

  .react-datepicker__day--today {
    font-weight: 700;
    color: #00B5B8;
    background-color: #E0F2F3;
    border: 2px solid #00B5B8;

    &:hover {
      background-color: #00B5B8;
      color: white;
    }
  }

  .react-datepicker__day--outside-month {
    color: #9CA3AF;
    opacity: 0.5;
  }

  .react-datepicker__month-dropdown,
  .react-datepicker__year-dropdown {
    background-color: white;
    border: 1px solid #E5E7EB;
    border-radius: 0.375rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }

  .react-datepicker__month-option,
  .react-datepicker__year-option {
    font-size: 0.875rem;
    padding: 0.5rem;

    &:hover {
      background-color: #E0F2F3;
      color: #00B5B8;
    }
  }

  .react-datepicker__month-option--selected,
  .react-datepicker__year-option--selected {
    background-color: #00B5B8;
    color: white;
    font-weight: 600;
  }

  .react-datepicker__navigation {
    top: 1rem;
  }

  .react-datepicker__navigation-icon::before {
    border-color: white;
    border-width: 2px 2px 0 0;
    height: 7px;
    width: 7px;
  }

  .react-datepicker__month-container {
    padding: 0.5rem;
  }
`;

const PageContainer = styled.div`
  background-color: #FFFFFF;
  border-radius: 0.5rem;
  border: 1px solid #E5E7EB;
  padding: 2rem;
  margin-bottom: 2rem;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 400;
  color: #111827;
  margin-bottom: 2rem;
`;

const FilterSection = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr auto auto;
  grid-template-rows: repeat(2, auto);
  gap: 1rem;
  margin-bottom: 2rem;
  align-items: end;
`;

const GridItem = styled.div`
  grid-column: ${props => props.column};
  grid-row: ${props => props.row};
`;

const Label = styled.label`
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  display: block;
  margin-bottom: 0.5rem;
`;

const DateInputContainer = styled.div`
  position: relative;

  .react-datepicker-wrapper {
    width: 100%;
  }

  .react-datepicker__input-container {
    width: 100%;
  }
`;

const DateInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  padding-right: 2.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #374151;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }

  &::placeholder {
    color: #9CA3AF;
  }
`;

const CalendarIcon = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;
  pointer-events: none;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  
  &::placeholder {
    color: #9CA3AF;
  }
  
  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SelectInput = styled.select`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  background-color: white;
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: #00B5B8;
    box-shadow: 0 0 0 3px rgba(0, 181, 184, 0.1);
  }
`;

const SearchButton = styled.button`
  background-color: #00B5B8;
  color: white;
  border: none;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background-color: #0891B2;
  }
`;

const PrintButton = styled.button`
  background-color: #F9FAFB;
  color: #374151;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  padding: 0.75rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  width: 100%;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;

  &:hover {
    background-color: #F3F4F6;
  }
`;

const TemplateBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${props => props.disabled ? '#F9FAFB' : 'transparent'};
  border: 1px solid ${props => props.disabled ? '#D1D5DB' : '#00B5B8'};
  color: ${props => props.disabled ? '#9CA3AF' : '#00B5B8'};
  border-radius: 0px;
  padding: 0.25rem 0.5rem;
  margin: 0.125rem;
  font-size: 0.75rem;
  height: 30px;
  width: auto;
  position: relative;
  cursor: pointer;
  opacity: ${props => props.disabled ? 0.6 : 1};
`;

const TemplateBadgeText = styled.span`
  margin-right: 0.5rem;
`;

const TagBadge = styled.div`
  display: inline-flex;
  align-items: center;
  background-color: #D1FAE5;
  color: #065F46;
  border: 1px solid #6EE7B7;
  border-radius: 0.375rem;
  padding: 0.25rem 0.75rem;
  margin: 0.125rem;
  font-size: 0.75rem;
  height: auto;
  width: auto;
  font-weight: 500;
`;

const EditButton = styled.button`
  background-color: ${props => props.disabled ? '#F3F4F6' : '#FFFFFF'};
  color: ${props => props.disabled ? '#9CA3AF' : '#374151'};
  border: 1px solid ${props => props.disabled ? '#E5E7EB' : '#CFD3D4'};
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  margin: 0.125rem;

  &:hover {
    background-color: ${props => props.disabled ? '#F3F4F6' : '#F9FAFB'};
  }
`;

const RemoveButton = styled.button`
  background: ${props => props.disabled ? '#F3F4F6' : 'white'};
  border: 1px solid ${props => props.disabled ? '#D1D5DB' : '#00B5B8'};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  color: ${props => props.disabled ? '#9CA3AF' : '#00B5B8'};
  padding: 0;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  position: absolute;
  top: -8px;
  right: -8px;
  opacity: ${props => props.disabled ? 0.5 : 1};
  pointer-events: auto;

  &:hover {
    background: ${props => props.disabled ? '#F3F4F6' : '#F9FAFB'};
  }
`;

const ViewDataButton = styled.button`
  background-color: #FBFFFF;
  color: #9c9e9eff;
  border: 1px solid #CFD3D4;
  border-radius: 0.375rem;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  margin-bottom: 0.5rem;
  width: auto;
  white-space: nowrap;
  
  &:hover {
    background-color: #F3F4F6;
  }
`;

const StatusText = styled.div`
  font-size: 0.75rem;
  color: #374151;
  margin-bottom: 0.5rem;
  text-align: center;
`;

const StatusBadge = styled.div`
  display: inline-block;
  font-size: 14px;
  font-weight: 400;
  margin-bottom: 0.5rem;
  text-align: center;
  width: 100%;
  text-decoration: underline;
  background: none;
  border: none;
  padding: 0.25rem 0.5rem;
  min-height: 24px;
  color: ${props =>
    props.$status === 'รอการอนุมัติ' ? '#FF0404' :
    props.$status === 'อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว' ? '#00B5B8' :
    props.$status === 'มีการแก้ไขรอการอนุมัติอีกครั้ง' ? '#FF0404' : '#6F6F6F'
  };

  /* 🔍 DEBUG: Force visibility */
  opacity: 1 !important;
  visibility: visible !important;
`;

const ActionPrintButton = styled.button`
  background: none;
  color: ${props => props.disabled ? '#9CA3AF' : '#374151'};
  border: none;
  padding: 0.5rem 1rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  width: 100%;
  opacity: ${props => props.disabled ? 0.5 : 1};

  &:hover {
    color: ${props => props.disabled ? '#9CA3AF' : '#00B5B8'};
  }
`;

const ViewHiddenButton = styled.button`
  background-color: #FFF7ED;
  color: #EA580C;
  border: 1px solid #FDBA74;
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #FFEDD5;
    border-color: #FB923C;
  }
`;

const SendResultButton = styled.button`
  background-color: #00B5B8;
  color: #FFFFFF;
  border: 1px solid #00B5B8;
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  margin: 0.125rem;

  &:hover {
    background-color: #0891B2;
    border-color: #0891B2;
  }
`;

const ResendButton = styled.button`
  background-color: #1D87F0;
  color: #FFFFFF;
  border: 1px solid #1D87F0;
  border-radius: 0.375rem;
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  margin: 0.125rem;

  &:hover {
    background-color: #1565C0;
    border-color: #1565C0;
  }
`;

const HistoryButton = styled.button`
  background: none;
  border: none;
  color: #6F6F6F;
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
  text-decoration: underline;

  &:hover {
    color: #6F6F6F;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  gap: 1.5rem;
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid #E5E7EB;
  border-top: 4px solid #00B5B8;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 1rem;
  color: #6B7280;
  font-weight: 500;
`;

const TableContainer = styled.div`
  overflow-x: auto;
  margin-top: 2rem;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: #F9FAFB;
`;

const TableRow = styled.tr`
  border-bottom: 1px solid #eee;

  &:hover {
    background-color: #F9FAFB;
  }
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  font-size: 14px;
  font-weight: 400;
  color: #A3A3A3;
  text-align: center;
`;

const TableHeaderCell2 = styled.th`
  padding: 1rem;
  font-size: 14px;
  font-weight: 400;
  color: #A3A3A3;
  text-align: center;
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 14px;
  color: #6F6F6F;
  text-align: center;
  vertical-align: top;
`;

const ComparisonBadge = styled.div`
  font-size: 12px;
  font-weight: 500;
  text-align: center;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  display: inline-block;
  background-color: transparent;
  color: ${props => {
    if (props.status === 'high') return '#DC2626';
    if (props.status === 'low') return '#D97706';
    return '#059669';
  }};
`;

const HealthBook = () => {
  // ตั้งค่าเริ่มต้นเป็นวันที่ 02/01/2025 (วันที่มีข้อมูลล่าสุด) เหมือน Super Admin
  const latestDate = new Date(2025, 0, 2); // 0 = มกราคม, 2 = วันที่ 2
  const [startDate, setStartDate] = useState(latestDate);
  const [endDate, setEndDate] = useState(latestDate);
  const [searchCompany, setSearchCompany] = useState("");
  const [searchText, setSearchText] = useState("");
  const [status, setStatus] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isHealthResultModalOpen, setIsHealthResultModalOpen] = useState(false);
  const [isSendHealthResultModalOpen, setIsSendHealthResultModalOpen] = useState(false);
  const [isHealthReportModalOpen, setIsHealthReportModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [isHiddenTemplatesModalOpen, setIsHiddenTemplatesModalOpen] = useState(false);
  const [hiddenTemplates, setHiddenTemplates] = useState([]);
  const [selectedRecordId, setSelectedRecordId] = useState(null);
  const navigate = useNavigate();

  // แปลง Date object เป็น YYYY-MM-DD
  const formatDateToYYYYMMDD = (date) => {
    if (!date) return null;
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSearch = async () => {
    try {
      setLoading(true);

      // สร้าง filters สำหรับ MemberLabList API (เหมือน Super Admin)
      const filters = {};

      // แปลงวันที่จาก Date object เป็น yyyy-mm-dd ก่อนส่งไปที่ API
      if (startDate) filters.dateStart = formatDateToYYYYMMDD(startDate);
      if (endDate) filters.dateStop = formatDateToYYYYMMDD(endDate);
      if (searchCompany) filters.keywordCampaign = searchCompany;
      if (searchText) filters.keywordName = searchText;

      // เรียก API ด้วย filters
      await fetchHealthRecords(filters);

      // ถ้าต้องการ filter ตามสถานะเพิ่มเติม ให้ทำฝั่ง client
      if (status) {
        const filtered = allHealthRecords.filter(record => {
          let statusEnum = 'pending';
          if (status === 'อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว') {
            statusEnum = 'approved';
          } else if (status === 'มีการแก้ไขรอการอนุมัติอีกครั้ง') {
            statusEnum = 'revised';
          }
          return record.approvalStatus === statusEnum;
        });
        setHealthRecords(filtered);
      }
    } catch (error) {
      console.error('Error searching:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = (recordId) => {
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      setIsHealthReportModalOpen(true);
    }
  };

  const handleViewData = (recordId) => {
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      setIsHealthReportModalOpen(true);
    }
  };

  const handleRemoveTemplate = async (recordId, templateIndex) => {
    const record = healthRecords.find(r => r.id === recordId);
    if (!record) return;

    const templateName = record.templates[templateIndex];
    const matchedTemplate = record.matchedTemplates?.find(t => t.template_name === templateName);

    if (!matchedTemplate || !matchedTemplate.template_id) {
      console.error('Cannot find template ID for:', templateName);
      return;
    }

    try {
      // เรียก API เพื่อซ่อน template (persist ลง database)
      await healthRecordsAPI.hideTemplate(
        recordId,
        matchedTemplate.template_id,
        templateName
      );

      // อัพเดท local state หลังจาก API สำเร็จ
      const updatedRecords = healthRecords.map(r => {
        if (r.id === recordId) {
          const updatedTemplates = r.templates.filter((_, index) => index !== templateIndex);
          const updatedMatchedTemplates = r.matchedTemplates?.filter((_, index) => index !== templateIndex);
          const updatedTags = [];
          updatedMatchedTemplates?.forEach(t => {
            updatedTags.push(...(t.tags || []));
          });

          return {
            ...r,
            templates: updatedTemplates,
            matchedTemplates: updatedMatchedTemplates,
            tags: [...new Set(updatedTags)]
          };
        }
        return r;
      });

      setHealthRecords(updatedRecords);
      setAllHealthRecords(updatedRecords);

    } catch (error) {
      console.error('❌ Error hiding template:', error);
    }
  };

  const handleViewHiddenTemplates = async (recordId) => {
    try {
      setSelectedRecordId(recordId);
      const response = await healthRecordsAPI.getHiddenTemplates(recordId);

      if (response.status === 'success' && Array.isArray(response.data)) {
        setHiddenTemplates(response.data);
        setIsHiddenTemplatesModalOpen(true);
      } else {
        console.error('❌ ไม่สามารถดึงข้อมูล Templates ที่ซ่อนได้');
      }
    } catch (error) {
      console.error('❌ Error fetching hidden templates:', error);
    }
  };

  const handleRestoreTemplate = async (templateId) => {
    if (!selectedRecordId) return;

    try {
      await healthRecordsAPI.unhideTemplate(selectedRecordId, templateId);

      // รีโหลดข้อมูล health records
      await fetchHealthRecords();

      // รีโหลดรายการ hidden templates
      const response = await healthRecordsAPI.getHiddenTemplates(selectedRecordId);
      if (response.status === 'success' && Array.isArray(response.data)) {
        setHiddenTemplates(response.data);

        // ถ้าไม่มี hidden templates แล้ว ปิด modal
        if (response.data.length === 0) {
          setIsHiddenTemplatesModalOpen(false);
        }
      }

    } catch (error) {
      console.error('❌ Error restoring template:', error);
    }
  };

  const handleSendResult = (recordId) => {
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      // ✅ เช็คจาก approvalStatus แทน isApproved เพื่อให้ถูกต้องกับสถานะ revised
      if (record.approvalStatus === 'approved') {
        // ถ้าเป็นการส่งใหม่ (สถานะ approved) แสดง SendHealthResultModal
        setIsSendHealthResultModalOpen(true);
      } else {
        // ถ้าเป็นการส่งครั้งแรก (สถานะ pending หรือ revised) แสดง HealthResultModal
        setIsHealthResultModalOpen(true);
      }
    }
  };

  const handleTemplateClick = async (templateName, matchedTemplateData = null) => {
    try {
      let template = null;

      // ถ้ามี matchedTemplateData ใช้เลย แต่ต้อง fetch รายละเอียดเพิ่ม
      if (matchedTemplateData && matchedTemplateData.template_id) {
        const response = await templateAPI.getById(matchedTemplateData.template_id);
        if (response.status === 'success' && response.data) {
          template = response.data;
        }
      } else {
        // ถ้าไม่มี ให้ค้นหาจาก template name
        const response = await templateAPI.getAll();
        if (response.status === 'success' && response.data) {
          template = response.data.find(t => t.template_name === templateName);
        }
      }

      if (template) {
        // แปลงข้อมูลให้ตรงกับ format ที่ TemplateDetailModal ต้องการ
        const formattedTemplate = {
          templateName: template.template_name,
          createdBy: template.created_by,
          tags: template.tags || [],
          gender: template.gender === 'male' ? 'ชาย' : template.gender === 'female' ? 'หญิง' : 'ทั้งสองเพศ',
          ageRange: `${template.age_min}-${template.age_max}`,
          parameters: template.parameters?.map(p => p.parameter_name) || [],
          normalValues: template.parameters?.map(p => p.normal_range || '-') || [],
          setValues: template.parameters?.map(p => `${p.custom_min}-${p.custom_max}`) || []
        };

        setSelectedTemplate(formattedTemplate);
        setIsModalOpen(true);
      } else {
        console.warn('Template not found:', templateName);
      }
    } catch (error) {
      console.error('Error loading template:', error);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedTemplate(null);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setSelectedRecord(null);
  };

  const handleEdit = (recordId) => {
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      setSelectedRecord(record);
      setSelectedRecordId(record.dbRecordId); // ✓ ใช้ database record_id
      setIsEditModalOpen(true);
    }
  };

  const handleCloseHealthResultModal = () => {
    setIsHealthResultModalOpen(false);
    setSelectedRecord(null);
  };

  const handleApprove = async (recordId) => {
    // Reload ข้อมูลใหม่จาก database เพื่อให้ได้ approval_status ที่ถูกต้อง
    // ต้องส่ง filters เดิมไปด้วย
    const filters = {
      dateStart: formatDateToYYYYMMDD(startDate),
      dateStop: formatDateToYYYYMMDD(endDate)
    };
    if (searchCompany) filters.keywordCampaign = searchCompany;
    if (searchText) filters.keywordName = searchText;

    await fetchHealthRecords(filters);
  };

  const handleViewHistory = (recordId) => {
    const record = healthRecords.find(r => r.id === recordId);
    if (record) {
      navigate('/health-records-history', { state: { selectedRecord: record } });
    }
  };

  const handleCloseSendHealthResultModal = () => {
    setIsSendHealthResultModalOpen(false);
    setSelectedRecord(null);
  };

  const handleConfirmSendAgain = async () => {
    if (selectedRecord) {
      try {
        const recordIdToUse = selectedRecord.dbRecordId || selectedRecord.id;

        const response = await healthRecommendationAPI.getDraft(recordIdToUse);

        if (response.status === 'success' && response.data) {
          const saveResponse = await healthRecommendationAPI.saveDraft(
            recordIdToUse,
            {
              doctor_advice: response.data.doctor_advice || '',
              nutrition_advice: response.data.nutrition_advice || '',
              exercise_advice: response.data.exercise_advice || '',
              sleep_advice: response.data.sleep_advice || ''
            },
            'admin'
          );

          if (saveResponse.status === 'success') {
            const filters = {
              dateStart: formatDateToYYYYMMDD(startDate),
              dateStop: formatDateToYYYYMMDD(endDate)
            };
            if (searchCompany) filters.keywordCampaign = searchCompany;
            if (searchText) filters.keywordName = searchText;
            await fetchHealthRecords(filters);
          }
        }
      } catch (error) {
        console.error('❌ Error changing status to revised:', error);
      }
    }
    setIsSendHealthResultModalOpen(false);
    setIsHealthResultModalOpen(true);
  };

  const handleCloseHealthReportModal = () => {
    setIsHealthReportModalOpen(false);
    setSelectedRecord(null);
  };

  // State สำหรับเก็บข้อมูลจาก API
  const [healthRecords, setHealthRecords] = useState([]);
  const [allHealthRecords, setAllHealthRecords] = useState([]); // เก็บข้อมูลทั้งหมดไว้สำหรับ filter
  const [loading, setLoading] = useState(false);
  const [masterData, setMasterData] = useState(null);

  // โหลด Master Data จาก API (เหมือน Super Admin)
  useEffect(() => {
    const loadMasterData = async () => {
      try {
        const result = await referenceDataAPI.getAllParameters();
        if (result.status === 'success' && result.data) {
          setMasterData(result.data);
        }
      } catch (error) {
        console.error('Error loading master data:', error);
      }
    };
    loadMasterData();
  }, []);

  // ฟังก์ชันหาค่าปกติจาก Master Data (เหมือน Super Admin)
  const getNormalRangeFromMaster = (paramName, sex, age) => {
    if (!masterData) {
      return '-';
    }

    // แปลงชื่อ parameter ให้ตรงกับ key ใน medium.json
    let searchName = paramName;

    // ต้องแปลง:
    if (paramName === 'SGOT (AST)') searchName = 'SGOT (AST)';
    else if (paramName === 'SGPT (ALT)') searchName = 'SGPT (ALT)';
    else if (paramName === 'ALP (Alkaline Phosphatase)') searchName = 'ALP';
    else if (paramName === 'ALP') searchName = 'ALP';
    else if (paramName === 'Uric Acid') searchName = 'Uric Acid';
    else if (paramName === 'Glucose') searchName = 'Glucose';
    else if (paramName === 'Protein') searchName = 'Protein';
    else if (paramName === 'CHO') searchName = 'คอเลสเตอรอลรวม';
    else if (paramName === 'TRI') searchName = 'ไตรกลีเซอไรด์';
    else if (paramName === 'FBS') searchName = 'Glucose';
    else if (paramName === 'BUN') searchName = 'BUN, Urea';
    else if (paramName === 'GGT') searchName = 'GGT';

    for (const category in masterData) {
      const categoryData = masterData[category];
      if (categoryData[searchName]) {
        const ranges = categoryData[searchName];
        for (const range of ranges) {
          const sexMatch = range.sex === 'F,M' || range.sex === sex;
          const ageMatch = age >= range.age_min && age <= range.age_max;

          if (sexMatch && ageMatch) {
            return range.normal_range;
          }
        }
      }
    }
    return '-';
  };

  // ฟังก์ชันเปรียบเทียบค่าปกติกับค่าที่ตรวจวัดได้
  const compareValues = (normalRange, resultValue) => {
    if (!normalRange || !resultValue || normalRange === 'xxx' || resultValue === 'xxx' || normalRange === '-' || resultValue === '-') {
      return { status: 'normal', label: '-' };
    }

    // แปลงค่าที่ตรวจวัดได้เป็นตัวเลข
    const result = parseFloat(resultValue);
    if (isNaN(result)) {
      return { status: 'normal', label: '-' };
    }

    // แยกช่วงค่าปกติ (เช่น "100-200" หรือ "<100" หรือ ">100")
    if (normalRange.includes('-')) {
      const [min, max] = normalRange.split('-').map(v => parseFloat(v.trim()));
      if (result < min) {
        return { status: 'low', label: 'Low' };
      } else if (result > max) {
        return { status: 'high', label: 'High' };
      } else {
        return { status: 'normal', label: 'Normal' };
      }
    } else if (normalRange.startsWith('<')) {
      const max = parseFloat(normalRange.replace('<', '').trim());
      if (result >= max) {
        return { status: 'high', label: 'High' };
      } else {
        return { status: 'normal', label: 'Normal' };
      }
    } else if (normalRange.startsWith('>')) {
      const min = parseFloat(normalRange.replace('>', '').trim());
      if (result <= min) {
        return { status: 'low', label: 'Low' };
      } else {
        return { status: 'normal', label: 'Normal' };
      }
    }

    return { status: 'normal', label: 'Normal' };
  };

  // ดึงข้อมูลจาก Database เมื่อ component โหลด
  // ดึงข้อมูลจาก MFU API เมื่อ component โหลด - แสดงข้อมูลวันที่ล่าสุดทันที (เหมือน Super Admin)
  // รอให้ master data โหลดเสร็จก่อน
  useEffect(() => {
    if (masterData) {
      const filters = {
        dateStart: formatDateToYYYYMMDD(latestDate),
        dateStop: formatDateToYYYYMMDD(latestDate)
      };
      fetchHealthRecords(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterData]);

  // Auto-refresh เมื่อกลับมาที่หน้านี้ (เช่น หลังจากไปหน้าอื่นแล้วกลับมา)
  useEffect(() => {
    // ✅ โหลดข้อมูลทันทีเมื่อกลับมาที่หน้านี้
    if (masterData && healthRecords.length === 0) {
      const filters = {
        dateStart: formatDateToYYYYMMDD(latestDate),
        dateStop: formatDateToYYYYMMDD(latestDate)
      };
      fetchHealthRecords(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterData, healthRecords.length]);

  // Auto-refresh เมื่อ tab กลับมา active
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && masterData) {
        // เมื่อ tab กลับมา active ให้ reload ข้อมูลด้วย filters ปัจจุบัน
        const filters = {
          dateStart: formatDateToYYYYMMDD(startDate),
          dateStop: formatDateToYYYYMMDD(endDate)
        };
        if (searchCompany) filters.company = searchCompany;
        if (searchText) filters.search = searchText;
        if (status) filters.status = status;
        fetchHealthRecords(filters);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterData, startDate, endDate, searchCompany, searchText, status]);

  // ฟังก์ชัน Match templates กับ health records
  const matchTemplatesWithRecords = async (records) => {
    try {
      // ดึง templates ทั้งหมดจาก Backend
      const templatesResponse = await templateAPI.getAll();

      if (templatesResponse.status !== 'success' || !templatesResponse.data) {
        return;
      }

      const allTemplates = templatesResponse.data;

      // Match แต่ละ record กับ templates
      for (const record of records) {
        let manualTemplates = null;
        if (record.dbRecordId) {
          try {
            const manualResponse = await healthRecordsAPI.getManualTemplates(record.dbRecordId);
            if (manualResponse.status === 'success' && manualResponse.data && manualResponse.data.length > 0) {
              manualTemplates = manualResponse.data;
            }
          } catch (err) {
            // No manual templates found
          }
        }

        const matchedTemplates = [];
        const allTags = new Set();

        // ถ้ามี manual templates ให้ใช้ manual templates
        if (manualTemplates && manualTemplates.length > 0) {
          matchedTemplates.push(...manualTemplates);
          manualTemplates.forEach(template => {
            if (template.tags && Array.isArray(template.tags)) {
              template.tags.forEach(tag => allTags.add(tag));
            }
          });
        }
        // ถ้าไม่มี manual templates ให้ทำ auto-match
        else {

        // แปลง gender เพื่อ match
        const recordGender = record.gender === 'ชาย' ? 'male' : 'female';
        const recordAge = parseInt(record.age) || 0;

        // สร้าง set ของ parameter names ที่มีใน record
        const recordParameterNames = new Set(record.parameters.map(p => p.name));

        for (const template of allTemplates) {
          // ตรวจสอบว่า template active หรือไม่
          if (template.is_active !== 1) continue;

          // ตรวจสอบ gender
          const templateGender = template.gender;
          if (templateGender !== 'both' && templateGender !== recordGender) continue;

          // ตรวจสอบ age range
          if (recordAge < template.age_min || recordAge > template.age_max) continue;

          // ตรวจสอบว่ามี parameters ตรงกันหรือไม่
          const templateParams = template.parameters || [];
          let matchCount = 0;
          let totalParams = templateParams.length;

          for (const templateParam of templateParams) {
            if (recordParameterNames.has(templateParam.parameter_name)) {
              // เช็คว่าค่าอยู่ในช่วงที่กำหนดหรือไม่
              const recordParam = record.parameters.find(p => p.name === templateParam.parameter_name);
              if (recordParam && recordParam.result) {
                const value = parseFloat(recordParam.result);
                const customMin = parseFloat(templateParam.custom_min);
                const customMax = parseFloat(templateParam.custom_max);

                if (!isNaN(value) && !isNaN(customMin) && !isNaN(customMax)) {
                  // ต้องอยู่ในช่วงที่กำหนด (custom_min - custom_max) ถึงจะ match
                  if (value >= customMin && value <= customMax) {
                    matchCount++;
                  }
                }
              }
            }
          }

          // ถ้า match มากกว่า 50% ถือว่า match
          if (totalParams > 0 && (matchCount / totalParams) >= 0.5) {
            matchedTemplates.push({
              template_id: template.id,
              template_name: template.template_name,
              match_percentage: Math.round((matchCount / totalParams) * 100),
              tags: template.tags || []
            });

            // เก็บ tags
            if (template.tags && Array.isArray(template.tags)) {
              template.tags.forEach(tag => allTags.add(tag));
            }
          }
        }
        } // ปิด else block ของ auto-match

        // อัพเดท record ด้วย matched templates และ tags
        record.matchedTemplates = matchedTemplates;
        record.tags = Array.from(allTags);
        record.templates = matchedTemplates.map(mt => mt.template_name);
      }

    } catch (error) {
      console.error('❌ Error matching templates:', error);
    }
  };

  const fetchHealthRecords = async (filters = {}) => {
    try {
      setLoading(true);

      // 🔹 Option 2: Hybrid System
      // ✅ ดึงข้อมูล Lab Results จาก MFU API โดยตรง (เหมือน Super Admin)
      const response = await memberAPI.getLabList(filters);

      // MFU API ใช้ response.result และ response.list แทน response.status และ response.data
      if (response.result && Array.isArray(response.list)) {
        // แปลงข้อมูลจาก MFU API ให้ตรงกับรูปแบบที่ UI ใช้
        const formattedData = await Promise.all(response.list.map(async (record, index) => {
          const profile = record.profile || {};
          const dataLab = record.data_lab || [];

          // แปลง data_lab array เป็น object เพื่อให้เข้าถึงได้ง่าย
          const labData = {};
          dataLab.forEach(item => {
            labData[item.name] = item.value;
          });

          // ดึงอายุและเพศสำหรับ fallback ค่าปกติ
          // ลำดับความสำคัญ: 1. profile.age, 2. คำนวณจาก birthday, 3. ค่า default 0
          const calculateAge = (birthday) => {
            if (!birthday) return null;
            const birthDate = new Date(birthday);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
              age--;
            }
            return age;
          };

          const age = profile.age ? parseInt(profile.age) : (calculateAge(profile.birthday) ?? 0);
          const sex = profile.gender === '1' ? 'M' : 'F';

          // แปลงข้อมูล Lab Results เป็น parameters สำหรับแสดงในตาราง
          const parameters = [];
          const testsNeedingNormalRange = []; // เก็บรายการที่ต้องดึงค่าปกติจาก medium.json

          // CBC - Complete Blood Count
          // ✅ ใช้ master data เสมอ - ไม่ใช้ค่าปกติจาก MFU API
          if (labData['wbc']) {
            const param = {
              name: 'WBC',
              result: labData['wbc'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'WBC', sex, age });
          }

          if (labData['hb']) {
            const param = {
              name: 'Hemoglobin',
              result: labData['hb'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'Hemoglobin', sex, age });
          }

          if (labData['hct']) {
            const param = {
              name: 'Hematocrit',
              result: labData['hct'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'Hematocrit', sex, age });
          }

          if (labData['mcv']) {
            const param = {
              name: 'MCV',
              result: labData['mcv'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'MCV', sex, age });
          }

          if (labData['mch']) {
            const param = {
              name: 'MCH',
              result: labData['mch'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'MCH', sex, age });
          }

          if (labData['mchc']) {
            const param = {
              name: 'MCHC',
              result: labData['mchc'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'MCHC', sex, age });
          }

          if (labData['plt']) {
            const param = {
              name: 'Platelets',
              result: labData['plt'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'Platelets', sex, age });
          }

          if (labData['neutrophile']) {
            const param = {
              name: 'Neutrophile',
              result: labData['neutrophile'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'Neutrophile', sex, age });
          }

          if (labData['lymphocyte_count']) {
            const param = {
              name: 'Lymphocyte',
              result: labData['lymphocyte_count'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'Lymphocyte', sex, age });
          }

          if (labData['monocyte_count']) {
            const param = {
              name: 'Monocyte',
              result: labData['monocyte_count'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'Monocyte', sex, age });
          }

          // Kidney Function Tests
          // ✅ ใช้ master data เสมอ - ไม่ใช้ค่าปกติจาก MFU API
          if (labData['creatinine']) {
            const param = {
              name: 'Creatinine',
              result: labData['creatinine'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'Creatinine', sex, age });
          }

          if (labData['creatinineegfrresult']) {
            const param = {
              name: 'eGFR',
              result: labData['creatinineegfrresult'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'eGFR', sex, age });
          }

          if (labData['uric']) {
            const param = {
              name: 'Uric Acid',
              result: labData['uric'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'Uric Acid', sex, age });
          }

          // Diabetes Tests
          // ✅ ใช้ master data เสมอ - ไม่ใช้ค่าปกติจาก MFU API
          if (labData['glucoseresult']) {
            const param = {
              name: 'Glucose',
              result: labData['glucoseresult'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'Glucose', sex, age });
          }

          if (labData['hba1c']) {
            const param = {
              name: 'HbA1C',
              result: labData['hba1c'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'HbA1C', sex, age });
          }

          // Liver Function Tests
          // ✅ ใช้ master data เสมอ - ไม่ใช้ค่าปกติจาก MFU API
          if (labData['sgot']) {
            const param = {
              name: 'SGOT (AST)',
              result: labData['sgot'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'SGOT (AST)', sex, age });
          }

          if (labData['liverfunctiontestalk']) {
            const param = {
              name: 'ALP (Alkaline Phosphatase)',
              result: labData['liverfunctiontestalk'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'ALP', sex, age });
          }

          // Note: MFU API ไม่มีฟิลด์ Albumin, Total Protein, Globulin, Bilirubin, GGT, HDL, LDL, Cholesterol, Triglyceride
          // ถ้ามีในอนาคตจะต้องเพิ่มเข้ามา

          // Urinalysis
          // ✅ ใช้ master data เสมอ - ไม่ใช้ค่าปกติจาก MFU API
          if (labData['urinalysissugarresul']) {
            const param = {
              name: 'Urine Glucose',
              result: labData['urinalysissugarresul'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'Glucose', sex, age });
          }

          if (labData['urinalysisproteinres']) {
            const param = {
              name: 'Urine Protein',
              result: labData['urinalysisproteinres'],
              normal: null  // บังคับให้ดึงจาก master data
            };
            parameters.push(param);
            testsNeedingNormalRange.push({ param, name: 'Protein', sex, age });
          }

          // ✅ ดึงค่าปกติจาก master data (medium.json) สำหรับทุก parameters
          if (testsNeedingNormalRange.length > 0 && masterData) {
            testsNeedingNormalRange.forEach(test => {
              const normalRange = getNormalRangeFromMaster(test.name, test.sex, test.age);
              if (test.param) {
                test.param.normal = normalRange;
              }
            });
          }

          // ใส่ค่า default '-' สำหรับ parameters ที่ยังไม่มีค่าปกติ
          parameters.forEach(param => {
            if (!param.normal) {
              param.normal = '-';
            }
          });

          // แปลงวันที่จาก YYYY-MM-DD HH:MM:SS เป็น DD-MM-YYYY
          const formatDate = (dateString) => {
            if (!dateString || dateString === '-') return '-';
            // ถ้าเป็นรูปแบบ "YYYY-MM-DD HH:MM:SS" ให้ตัดเอาแค่วันที่
            const datePart = dateString.split(' ')[0];
            const parts = datePart.split('-');
            if (parts.length === 3) {
              return `${parts[2]}-${parts[1]}-${parts[0]}`;
            }
            return dateString;
          };

          // โหลด approval_status และคำแนะนำจาก health_recommendations (เหมือน Super Admin)
          let approvalStatus = 'pending';
          let approvedAt = null;
          let nutritionAdvice = '';
          let exerciseAdvice = '';
          let sleepAdvice = '';
          let doctorAdvice = '';

          let dbRecordId = null;

          try {
            const recResponse = await healthRecommendationAPI.getDraft(record.memberid);
            if (recResponse.status === 'success' && recResponse.data) {
              dbRecordId = recResponse.data.record_id; // ✅ ถูกต้อง: ใช้ record_id ไม่ใช่ id
              approvalStatus = recResponse.data.approval_status || 'pending';
              approvedAt = recResponse.data.approved_at;
              nutritionAdvice = recResponse.data.nutrition_advice || '';
              exerciseAdvice = recResponse.data.exercise_advice || '';
              sleepAdvice = recResponse.data.sleep_advice || '';
              doctorAdvice = recResponse.data.doctor_advice || '';
            }
          } catch (err) {
            // No recommendations yet
          }

          return {
            id: record.memberid,
            dbRecordId: dbRecordId, // database record_id สำหรับการบันทึก templates/drafts
            no: index + 1,
            date: formatDate(record.dateadd),
            patientName: `${profile.nameprefix || ''} ${profile.name || ''} ${profile.lastname || ''}`.trim() || '-',
            gender: profile.gender === '1' ? 'ชาย' : 'หญิง',
            age: age || '-',
            diseases: profile.congenitaldisease || 'ไม่มี',
            company: labData['company'] || '-',
            foodAllergies: labData['food_allergies'] || labData['food allergies'] || '-',
            hn: record.memberid || '-',  // ใช้ memberid เป็น HN
            parameters: parameters,
            templates: [],
            matchedTemplates: [],
            tags: [],
            approvalStatus: approvalStatus,
            approvedAt: approvedAt,
            isApproved: approvalStatus === 'approved',
            // ข้อมูลเพิ่มเติมสำหรับ PDF
            labData: labData, // ข้อมูล lab ทั้งหมด
            weight: labData['weight'] || '-',
            height: labData['height'] || '-',
            bmi: labData['bmi'] || '-',
            bloodPressure: (labData['sys'] && labData['dia']) ? `${labData['sys']}/${labData['dia']}` : (labData['sys/dias'] || '-'),
            pulse: labData['pulse'] || '-',
            doctorNote: doctorAdvice || labData['doctornote'] || '-',
            // คำแนะนำจาก AI (เหมือน Super Admin)
            nutritionAdvice: nutritionAdvice,
            exerciseAdvice: exerciseAdvice,
            sleepAdvice: sleepAdvice
          };
        }));

        await matchTemplatesWithRecords(formattedData);

        setHealthRecords(formattedData);
        setAllHealthRecords(formattedData);
      } else {
        setHealthRecords([]);
        setAllHealthRecords([]);
      }
    } catch (err) {
      console.error('❌ Error fetching health records:', err);
      setHealthRecords([]);
      setAllHealthRecords([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <DatePickerStyles />
      <PageContainer>
        <PageTitle>สมุดสุขภาพ</PageTitle>

        <FilterSection>
          {/* แถวที่ 1 - Labels */}
          <GridItem column="1" row="1">
            <Label>วันที่ตรวจเริ่ม</Label>
          </GridItem>
          <GridItem column="2" row="1">
            <Label>ถึง</Label>
          </GridItem>
          <GridItem column="3" row="1">
            <Label>ค้นหาชื่อ-สกุล, HN</Label>
          </GridItem>
          <GridItem column="4" row="1">
            <Label>บริษัท</Label>
          </GridItem>
          <GridItem column="5" row="1">
            <Label>สถานะ</Label>
          </GridItem>

          {/* แถวที่ 2 - Input Fields และ Buttons */}
          <GridItem column="1" row="2">
            <DateInputContainer>
              <DatePicker
                selected={startDate}
                onChange={(date) => setStartDate(date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                customInput={<DateInput />}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                maxDate={new Date()}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </GridItem>

          <GridItem column="2" row="2">
            <DateInputContainer>
              <DatePicker
                selected={endDate}
                onChange={(date) => setEndDate(date)}
                dateFormat="dd/MM/yyyy"
                placeholderText="dd/mm/yyyy"
                customInput={<DateInput />}
                showYearDropdown
                showMonthDropdown
                dropdownMode="select"
                maxDate={new Date()}
                minDate={startDate}
              />
              <CalendarIcon>
                <Calendar size={16} />
              </CalendarIcon>
            </DateInputContainer>
          </GridItem>

          <GridItem column="3" row="2">
            <TextInput
              type="text"
              placeholder="พิมพ์ชื่อหรือ HN"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
          </GridItem>

          <GridItem column="4" row="2">
            <TextInput
              type="text"
              placeholder="ค้นหาบริษัท"
              value={searchCompany}
              onChange={(e) => setSearchCompany(e.target.value)}
            />
          </GridItem>

          <GridItem column="5" row="2">
            <SelectInput
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">เลือกสถานะ</option>
              <option value="รอการอนุมัติ">รอการอนุมัติ</option>
              <option value="อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว">อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว</option>
              <option value="มีการแก้ไขรอการอนุมัติอีกครั้ง">มีการแก้ไขรอการอนุมัติอีกครั้ง</option>
            </SelectInput>
          </GridItem>

          <GridItem column="6" row="2">
            <SearchButton onClick={handleSearch}>
              ค้นหา
            </SearchButton>
          </GridItem>

          <GridItem column="7" row="2">
            <PrintButton onClick={handlePrint}>
              <Printer size={16} />
              Print
            </PrintButton>
          </GridItem>
        </FilterSection>

        {loading ? (
          <LoadingContainer>
            <LoadingSpinner />
            <LoadingText>กำลังโหลด...</LoadingText>
          </LoadingContainer>
        ) : (
          <TableContainer>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHeaderCell rowSpan="2" style={{ width: '50px' }}>ที่</TableHeaderCell>
                <TableHeaderCell rowSpan="2" style={{ width: '100px' }}>วันที่ตรวจ</TableHeaderCell>
                <TableHeaderCell rowSpan="2" style={{ width: '180px' }}>ชื่อผู้รับการตรวจ/เพศ/อายุ/โรคประจำตัว</TableHeaderCell>
                <TableHeaderCell rowSpan="2" style={{ width: '120px' }}>HN</TableHeaderCell>
                <TableHeaderCell colSpan="4">ผลการตรวจสุขภาพ</TableHeaderCell>
                <TableHeaderCell rowSpan="2" style={{ width: '200px' }}>การแปลผลสุขภาพที่กำหนดไว้เบื้องต้น/ Doctor Note</TableHeaderCell>
                <TableHeaderCell rowSpan="2" style={{ width: '150px' }}>การจัดการข้อมูล</TableHeaderCell>
              </TableRow>
              <TableRow>
                <TableHeaderCell2 style={{ width: '150px' }}>พารามิเตอร์</TableHeaderCell2>
                <TableHeaderCell2 style={{ width: '150px' }}>ค่าปกติ</TableHeaderCell2>
                <TableHeaderCell2 style={{ width: '120px' }}>ค่าที่ตรวจวัดได้</TableHeaderCell2>
                <TableHeaderCell2 style={{ width: '50px' }}></TableHeaderCell2>
              </TableRow>
            </TableHeader>
            <tbody>
              {healthRecords.map((record) => (
                <TableRow key={record.id}>
                  <TableCell>{record.no}</TableCell>
                  <TableCell>{record.date}</TableCell>
                  <TableCell style={{ textAlign: 'left' }}>
                    <div><span style={{ fontWeight: '700' }}>ชื่อ:</span> <span style={{ fontWeight: '400' }}>{record.patientName}</span></div>
                    <div><span style={{ fontWeight: '700' }}>เพศ:</span> {record.gender}</div>
                    <div><span style={{ fontWeight: '700' }}>อายุ:</span> {record.age} ปี</div>
                    <div><span style={{ fontWeight: '700' }}>โรคประจำตัว:</span></div>
                    <div style={{ fontWeight: '400' }}>{record.diseases}</div>
                    <div><span style={{ fontWeight: '700' }}>บริษัท:</span> <span style={{ fontWeight: '400' }}>{record.company}</span></div>
                  </TableCell>
                  <TableCell>{record.hn}</TableCell>
                  <TableCell colSpan="4" style={{ padding: 0 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <tbody>
                        {record.parameters.map((param, index) => {
                          const comparison = compareValues(param.normal, param.result);
                          return (
                            <tr key={index}>
                              <td style={{
                                padding: '0.125rem 1rem',
                                textAlign: 'left',
                                fontSize: '14px',
                                color: '#6F6F6F',
                                width: '150px'
                              }}>
                                {param.name}
                              </td>
                              <td style={{
                                padding: '0.125rem 1rem',
                                textAlign: 'center',
                                fontSize: '14px',
                                color: '#6F6F6F',
                                whiteSpace: 'nowrap',
                                width: '150px'
                              }}>
                                {param.normal}
                              </td>
                              <td style={{
                                padding: '0.125rem 0.5rem',
                                textAlign: 'center',
                                fontSize: '14px',
                                color: '#6F6F6F',
                                width: '120px'
                              }}>
                                {param.result}
                              </td>
                              <td style={{
                                padding: '0.125rem 0.25rem',
                                textAlign: 'left',
                                fontSize: '14px',
                                color: '#6F6F6F',
                                width: '50px'
                              }}>
                                <ComparisonBadge status={comparison.status}>
                                  {comparison.label}
                                </ComparisonBadge>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </TableCell>
                  <TableCell style={{ textAlign: 'left' }}>
                    {/* แสดง Templates */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginBottom: '0.5rem' }}>
                      {record.templates.map((templateName, index) => {
                        // หา matched template data ที่ตรงกับ template name นี้
                        const matchedTemplate = record.matchedTemplates?.find(
                          t => t.template_name === templateName
                        );

                        // ✅ ปิดการแก้ไขเฉพาะเมื่อสถานะเป็น 'approved' เท่านั้น
                        const isDisabled = record.approvalStatus === 'approved';

                        return (
                          <TemplateBadge
                            key={index}
                            disabled={isDisabled}
                            onClick={() => handleTemplateClick(templateName, matchedTemplate)}
                          >
                            <TemplateBadgeText>{templateName}</TemplateBadgeText>
                            {!isDisabled && (
                              <RemoveButton
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveTemplate(record.id, index);
                                }}
                                title="ลบ template"
                              >
                                <X size={8} />
                              </RemoveButton>
                            )}
                          </TemplateBadge>
                        );
                      })}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        {/* ✅ ปุ่ม "แก้ไข" - ปิดการใช้งานเฉพาะเมื่อสถานะเป็น 'approved' */}
                        <EditButton
                          disabled={record.approvalStatus === 'approved'}
                          onClick={() => record.approvalStatus !== 'approved' && handleEdit(record.id)}
                        >
                          แก้ไข
                        </EditButton>
                        {/* ✅ ปุ่ม "ส่งใหม่" - แสดงเฉพาะเมื่อสถานะเป็น 'approved' */}
                        {/* ✅ ปุ่ม "กดเพื่อส่งผลสุขภาพ" - แสดงเมื่อสถานะเป็น 'pending' หรือ 'revised' */}
                        {record.approvalStatus === 'approved' ? (
                          <ResendButton onClick={() => handleSendResult(record.id)}>
                            ส่งใหม่
                          </ResendButton>
                        ) : (
                          <SendResultButton onClick={() => handleSendResult(record.id)}>
                            กดเพื่อส่งผลสุขภาพ
                          </SendResultButton>
                        )}
                      </div>
                      {/* ✅ ปุ่ม "ประวัติการส่งผลสุขภาพ" - แสดงเฉพาะเมื่อเคยอนุมัติแล้ว */}
                      {record.isApproved && (
                        <HistoryButton onClick={() => handleViewHistory(record.id)}>
                          <History />
                          ประวัติการส่งผลสุขภาพ
                        </HistoryButton>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' }}>
                      <ViewDataButton onClick={() => handleViewData(record.id)}>
                        ดูข้อมูล
                      </ViewDataButton>
                      <StatusText>สถานะจัดส่ง</StatusText>
                      <StatusBadge $status={
                        record.approvalStatus === 'approved' ? 'อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว' :
                        record.approvalStatus === 'revised' ? 'มีการแก้ไขรอการอนุมัติอีกครั้ง' :
                        'รอการอนุมัติ'
                      }>
                        {record.approvalStatus === 'approved' ? 'อนุมัติและจัดส่งผลการตรวจสุขภาพแล้ว' :
                         record.approvalStatus === 'revised' ? 'มีการแก้ไขรอการอนุมัติอีกครั้ง' :
                         'รอการอนุมัติ'}
                      </StatusBadge>
                      <ActionPrintButton
                        onClick={() => handlePrint(record.id)}
                        disabled={!record.isApproved}
                      >
                        <Printer size={14} />
                        Print
                      </ActionPrintButton>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </TableContainer>
        )}
      </PageContainer>

      <TemplateDetailModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        templateData={selectedTemplate}
      />

      <EditTemplateModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        recordId={selectedRecordId}
        selectedRecord={selectedRecord}
        onSave={fetchHealthRecords}
      />

      <HealthResultModal
        isOpen={isHealthResultModalOpen}
        onClose={handleCloseHealthResultModal}
        selectedRecord={selectedRecord}
        onApprove={handleApprove}
      />

      <SendHealthResultModal
        isOpen={isSendHealthResultModalOpen}
        onClose={handleCloseSendHealthResultModal}
        onConfirm={handleConfirmSendAgain}
      />

      <HealthReportModal
        isOpen={isHealthReportModalOpen}
        onClose={handleCloseHealthReportModal}
        recordData={selectedRecord}
      />

      {/* Hidden Templates Modal */}
      {isHiddenTemplatesModalOpen && (
        <HiddenTemplatesModal
          isOpen={isHiddenTemplatesModalOpen}
          onClose={() => setIsHiddenTemplatesModalOpen(false)}
          hiddenTemplates={hiddenTemplates}
          onRestore={handleRestoreTemplate}
        />
      )}
    </Layout>
  );
};

// Hidden Templates Modal Component
const HiddenTemplatesModal = ({ isOpen, onClose, hiddenTemplates, onRestore }) => {
  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Templates ที่ซ่อนไว้</ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={20} />
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          {hiddenTemplates.length === 0 ? (
            <EmptyMessage>ไม่มี Templates ที่ซ่อนไว้</EmptyMessage>
          ) : (
            <HiddenTemplatesList>
              {hiddenTemplates.map((template) => (
                <HiddenTemplateItem key={template.template_id}>
                  <TemplateInfo>
                    <TemplateName>{template.template_name}</TemplateName>
                    <TemplateDetails>
                      <div>ซ่อนเมื่อ: {new Date(template.hidden_at).toLocaleString('th-TH')}</div>
                      {template.tags && template.tags.length > 0 && (
                        <TagsContainer>
                          {template.tags.map((tag, index) => (
                            <Tag key={index}>{tag}</Tag>
                          ))}
                        </TagsContainer>
                      )}
                    </TemplateDetails>
                  </TemplateInfo>
                  <RestoreButton onClick={() => onRestore(template.template_id)}>
                    เอากลับมา
                  </RestoreButton>
                </HiddenTemplateItem>
              ))}
            </HiddenTemplatesList>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

// Modal Styled Components
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 0.5rem;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #E5E7EB;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6B7280;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #111827;
  }
`;

const ModalBody = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
`;

const EmptyMessage = styled.div`
  text-align: center;
  color: #6B7280;
  padding: 2rem;
`;

const HiddenTemplatesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const HiddenTemplateItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border: 1px solid #E5E7EB;
  border-radius: 0.375rem;
  background-color: #F9FAFB;
`;

const TemplateInfo = styled.div`
  flex: 1;
`;

const TemplateName = styled.div`
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.5rem;
`;

const TemplateDetails = styled.div`
  font-size: 0.875rem;
  color: #6B7280;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.5rem;
`;

const Tag = styled.span`
  background-color: #E0F2F1;
  color: #00796B;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
`;

const RestoreButton = styled.button`
  background-color: #00B5B8;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #009FA2;
  }
`;

export default HealthBook;